import React, { useState } from 'react';
import axios from 'axios';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button, Tabs, Tab,
    Box, Stack, Grid, Typography, TextField, Checkbox, RadioGroup,
    FormControlLabel, Radio
} from '@mui/material';

// --- HELPER FOR FORM ROWS ---
const ModalFormRow = ({ label, children, bold = false, note = "" }) => (
    <Grid container spacing={2} alignItems="center" sx={{ mb: 1 }}>
        <Grid item xs={12} md={5}>
            <Typography variant="subtitle2" sx={{ fontWeight: bold ? 'bold' : 'normal', display: 'block' }}>
                {label}
            </Typography>
            {note && (
                <Typography variant="caption" sx={{ fontStyle: 'italic', color: '#666', display: 'block', fontSize: '0.75rem' }}>
                    {note}
                </Typography>
            )}
        </Grid>
        <Grid item xs={12} md={7}>{children}</Grid>
    </Grid>
);

export default function AddPolicyDialog({ open, onClose, matterId, onSave }) {
    const [tabValue, setTabValue] = useState(0);
    const [formData, setFormData] = useState({});

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSave = async () => {
        try {
            await axios.post(`https://demo.insollex.com/api/legal-matter/${matterId}/policies`, formData);
            if (onSave) onSave();
            onClose();
        } catch (error) {
            console.error("Error saving policy:", error);
            alert("Failed to save policy.");
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 1 } }}>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
                <Box sx={{ width: 16, height: 16, borderRadius: '50%', bgcolor: '#4caf50', mr: 1 }} />
                Adding Claim (New)
            </DialogTitle>

            <DialogContent sx={{ p: 0, bgcolor: '#fdfdfd' }}>
                <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} variant="scrollable" sx={{ bgcolor: '#f0f0f0', minHeight: 32 }}>
                    <Tab label="Policies" sx={{ fontSize: '0.75rem', textTransform: 'none' }} />
                    <Tab label="Policies cont./Notes" sx={{ fontSize: '0.75rem', textTransform: 'none' }} />
                    <Tab label="Estate Duty" sx={{ fontSize: '0.75rem', textTransform: 'none' }} />
                    <Tab label="Estimated CGT" sx={{ fontSize: '0.75rem', textTransform: 'none' }} />
                </Tabs>

                <Box sx={{ p: 3, minHeight: '600px' }}>
                    {/* ================= TAB 1: POLICIES ================= */}
                    {tabValue === 0 && (
                        <Stack spacing={0.5}>
                            <ModalFormRow label="Insurance Policy Number and Description">
                                <TextField 
                                    name="description"
                                    onChange={handleChange}
                                    fullWidth 
                                    size="small" 
                                    sx={{ bgcolor: '#fffde7' }} // Matches image_052b98 yellow highlight
                                />
                            </ModalFormRow>

                            <ModalFormRow label="Name of Insurance Company">
                                <TextField name="companyName" onChange={handleChange} fullWidth size="small" />
                            </ModalFormRow>

                            <ModalFormRow label="Address of Insurance Company in block form">
                                <TextField name="companyAddress" onChange={handleChange} fullWidth multiline rows={2} size="small" />
                            </ModalFormRow>

                            <ModalFormRow label="Value of Policy as indicated on Inventory">
                                <TextField name="inventoryValue" onChange={handleChange} defaultValue="0.00" size="small" sx={{ width: '150px', '& .MuiInputBase-input': { textAlign: 'right' } }} />
                            </ModalFormRow>

                            <ModalFormRow label="Proceeds of policy paid out" bold>
                                <TextField name="proceeds" onChange={handleChange} defaultValue="0.00" size="small" sx={{ width: '150px', '& .MuiInputBase-input': { textAlign: 'right' } }} />
                            </ModalFormRow>

                            <ModalFormRow label="Annuity payment after death">
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <TextField name="annuityPayment" onChange={handleChange} defaultValue="0.00" size="small" sx={{ width: '150px', '& .MuiInputBase-input': { textAlign: 'right' } }} />
                                    <Typography variant="caption" sx={{ color: 'red' }}>(Calculation must be done in Estate Duty Tab)</Typography>
                                </Stack>
                            </ModalFormRow>

                            <ModalFormRow label="Value approved by SARS for Duty">
                                <TextField name="sarsValue" onChange={handleChange} defaultValue="0.00" size="small" sx={{ width: '150px', '& .MuiInputBase-input': { textAlign: 'right' } }} />
                            </ModalFormRow>

                            <Box sx={{ bgcolor: '#f9f9f9', p: 1, border: '1px solid #eee', mt: 1 }}>
                                <ModalFormRow label="IF PAID DIRECT: Full names of Beneficiary" bold>
                                    <Stack direction="row" spacing={1}>
                                        <TextField name="beneficiaryName" onChange={handleChange} fullWidth size="small" />
                                        <Button variant="contained" size="small" sx={{ bgcolor: '#e0e0e0', color: '#000', border: '1px solid #ccc' }}>Heirs</Button>
                                    </Stack>
                                </ModalFormRow>

                                <ModalFormRow label="Beneficiary's Birth Date">
                                    <TextField name="beneficiaryDob" onChange={handleChange} size="small" sx={{ width: '150px' }} placeholder="YYYY/MM/DD" />
                                </ModalFormRow>

                                <ModalFormRow label="Address of this beneficiary">
                                    <TextField name="beneficiaryAddress" onChange={handleChange} fullWidth size="small" />
                                </ModalFormRow>

                                <ModalFormRow label="Beneficiary's Gender">
                                    <RadioGroup row name="beneficiaryGender" onChange={handleChange}>
                                        <FormControlLabel value="Male" control={<Radio size="small" />} label={<Typography variant="caption">Male</Typography>} />
                                        <FormControlLabel value="Female" control={<Radio size="small" />} label={<Typography variant="caption">Female</Typography>} />
                                    </RadioGroup>
                                </ModalFormRow>

                                <ModalFormRow label="Is above beneficiary the surviving spouse?">
                                    <Checkbox name="isSpouseBeneficiary" onChange={handleChange} size="small" sx={{ p: 0 }} />
                                </ModalFormRow>
                            </Box>

                            <ModalFormRow label="Will the policy be collected as cash?">
                                <Checkbox name="isCollectedAsCash" onChange={handleChange} size="small" sx={{ p: 0 }} />
                            </ModalFormRow>

                            <ModalFormRow label="Has this policy been ceded to an heir?">
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <Checkbox name="isCeded" onChange={handleChange} size="small" sx={{ p: 0 }} />
                                    <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                                        (A cession is not the same as being the direct beneficiary in the policy!)
                                    </Typography>
                                </Stack>
                            </ModalFormRow>

                            <ModalFormRow label="If ceded, to whom?">
                                <Stack direction="row" spacing={1}>
                                    <TextField name="cededTo" onChange={handleChange} fullWidth size="small" />
                                    <Button variant="contained" size="small" sx={{ bgcolor: '#e0e0e0', color: '#000', border: '1px solid #ccc' }}>Heirs</Button>
                                </Stack>
                            </ModalFormRow>

                            <ModalFormRow label="Are proceeds recoverable by a spouse?" note="(In terms of an ante- or postnuptial contract)">
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <Checkbox name="recoverableBySpouse" onChange={handleChange} size="small" sx={{ p: 0 }} />
                                    <Typography variant="caption">Proceeds:</Typography>
                                    <TextField name="spouseProceeds" onChange={handleChange} defaultValue="0.00" size="small" sx={{ width: '100px' }} />
                                </Stack>
                            </ModalFormRow>

                            <ModalFormRow label="Are proceeds recoverable by child/children?" note="(In terms of an ante- or postnuptial contract)">
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <Checkbox name="recoverableByChildren" onChange={handleChange} size="small" sx={{ p: 0 }} />
                                    <Typography variant="caption">Proceeds:</Typography>
                                    <TextField name="childrenProceeds" onChange={handleChange} defaultValue="0.00" size="small" sx={{ width: '100px' }} />
                                </Stack>
                            </ModalFormRow>

                            <ModalFormRow label="Is this a 'partnership' policy?" note="(For which the premiums were NOT paid by Deceased)">
                                <Checkbox name="isPartnershipPolicy" onChange={handleChange} size="small" sx={{ p: 0 }} />
                            </ModalFormRow>
                        </Stack>
                    )}

                    {/* ================= TAB 2: POLICIES CONT./NOTES ================= */}
                    {tabValue === 1 && (
                <Stack spacing={0.5}>
                    <ModalFormRow 
                        label="Is this a deduction for a pension fund?" 
                        note="(Funds as defined in Sect 1 of Act 58 of 1962 or ito Sect 3(3)(a)bis(i) or (ii))"
                    >
                        <Checkbox name="isPensionFund" onChange={handleChange} size="small" sx={{ p: 0 }} />
                    </ModalFormRow>

                    <ModalFormRow label="Aggregate premiums paid by beneficiary:">
                        <Stack direction="row" spacing={1} alignItems="center">
                            <TextField name="premiumsPaid" onChange={handleChange} defaultValue="0.00" size="small" sx={{ width: '150px', '& .MuiInputBase-input': { textAlign: 'right' } }} />
                            <Typography variant="caption" sx={{ color: '#666' }}>
                                (Add interest at 6% p/a from payment date)
                            </Typography>
                        </Stack>
                    </ModalFormRow>

                    <ModalFormRow label="Consideration paid by beneficiary:">
                        <Stack direction="row" spacing={1} alignItems="center">
                            <TextField name="considerationPaid" onChange={handleChange} defaultValue="0.00" size="small" sx={{ width: '150px', '& .MuiInputBase-input': { textAlign: 'right' } }} />
                            <Typography variant="caption" sx={{ color: '#666' }}>
                                (Add interest at 6% p/a from payment date)
                            </Typography>
                        </Stack>
                    </ModalFormRow>

                    <Box sx={{ mt: 2 }}>
                        <Typography variant="caption" sx={{ fontWeight: 500, color: '#333', mb: 1, display: 'block' }}>
                            Notes to be printed on the L&D account about this asset:
                        </Typography>
                        <TextField 
                            name="notes"
                            onChange={handleChange}
                            fullWidth 
                            multiline 
                            rows={15} 
                            variant="outlined" 
                            sx={{ 
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 0,
                                    border: '1px solid #999'
                                }
                            }} 
                        />
                    </Box>

                    <Stack spacing={1} sx={{ mt: 2 }}>
                        <ModalFormRow label="Print above notes on the L&D a/c?">
                            <Checkbox name="printNotes" onChange={handleChange} size="small" sx={{ p: 0 }} />
                        </ModalFormRow>

                        <ModalFormRow label="Must this asset be excluded from the community estate?">
                            <Checkbox name="excludeFromCommunity" onChange={handleChange} size="small" sx={{ p: 0 }} />
                        </ModalFormRow>
                    </Stack>
                </Stack>
                    )}

                    {/* ================= TAB 3: ESTATE DUTY ================= */}
                    {tabValue === 2 && (
                        <>
        <Typography variant="caption" sx={{ fontWeight: 'bold', mb: 2, display: 'block', textAlign: 'center' }}>
            For Liquidation and Distribution as well as Estate Duty purposes:
        </Typography>
        
        <Stack spacing={0.5}>
            <ModalFormRow label="Is this claim iro a bequest made to charity/fund?">
                <Stack direction="row" spacing={1} alignItems="center">
                    <Checkbox name="isCharityBequest" onChange={handleChange} size="small" sx={{ p: 0 }} />
                    <Typography variant="caption">(Including public educational and religious institutions)</Typography>
                </Stack>
            </ModalFormRow>

            <ModalFormRow label="Is this claim iro a donation to Deceased by its beneficiary?">
                <Checkbox name="isDonationToDeceased" onChange={handleChange} size="small" sx={{ p: 0 }} />
            </ModalFormRow>

            <ModalFormRow label="Is this claim AGAINST the estate for accrual?">
                <Stack direction="row" spacing={1} alignItems="center">
                    <Checkbox name="isAccrualClaim" onChange={handleChange} size="small" sx={{ p: 0 }} />
                    <Typography variant="caption" sx={{ fontStyle: 'italic' }}>
                        (In terms of Sect. 3 of Matrimonial Property Act 88/1984)
                    </Typography>
                </Stack>
            </ModalFormRow>

            <ModalFormRow label="Is it iro an inheritance/donation made by a foreigner?">
                <Stack direction="row" spacing={1} alignItems="center">
                    <Checkbox name="isForeignInheritance" onChange={handleChange} size="small" sx={{ p: 0 }} />
                    <Typography variant="caption">(Deductable ito Sect 4(e))</Typography>
                </Stack>
            </ModalFormRow>

            <ModalFormRow label="Is it iro of credits/debts/rights of action OUTSIDE SA?">
                <Stack direction="row" spacing={1} alignItems="center">
                    <Checkbox name="isForeignDebt" onChange={handleChange} size="small" sx={{ p: 0 }} />
                    <Typography variant="caption">(This includes off-shore investment accounts)</Typography>
                </Stack>
            </ModalFormRow>

            <ModalFormRow label="Is this claim iro a gratuity/benefit society award?">
                <Stack direction="row" spacing={1} alignItems="center">
                    <Checkbox name="isGratuity" onChange={handleChange} size="small" sx={{ p: 0 }} />
                    <Typography variant="caption">(SA residents - enforceable inside and outside RSA)</Typography>
                </Stack>
            </ModalFormRow>

            <ModalFormRow label="Is it goodwill/licence/patent/design/trade mark/copyright?">
                <Stack direction="row" spacing={1} alignItems="center">
                    <Checkbox name="isGoodwill" onChange={handleChange} size="small" sx={{ p: 0 }} />
                    <Typography variant="caption">(NOT registered in South Africa)</Typography>
                </Stack>
            </ModalFormRow>

            <ModalFormRow label="Is it an annuity accruing to the heir to whom it is awarded?">
                <Checkbox name="isAnnuity" onChange={handleChange} size="small" sx={{ p: 0 }} />
            </ModalFormRow>

            <ModalFormRow label="Period for which annuity will be enjoyed">
                <Stack direction="row" spacing={1} alignItems="center">
                    <TextField name="annuityPeriod" onChange={handleChange} defaultValue="0" size="small" sx={{ width: '60px' }} />
                    <Typography variant="caption">years</Typography>
                </Stack>
            </ModalFormRow>

            <ModalFormRow label="Annual value of this annuity (in figures)">
                <TextField name="annuityValue" onChange={handleChange} defaultValue="0.00" size="small" sx={{ width: '150px', '& .MuiInputBase-input': { textAlign: 'right' } }} />
            </ModalFormRow>

            <ModalFormRow label="Yield to be used for Estate Duty calculation">
                <Stack direction="row" spacing={1} alignItems="center">
                    <TextField name="yield" onChange={handleChange} defaultValue="12.000000" size="small" sx={{ width: '120px' }} />
                    <Typography variant="caption">%</Typography>
                </Stack>
            </ModalFormRow>

            <ModalFormRow label="Value ito Sec. 5 of the Estate Duty Act">
                <Stack direction="row" spacing={1} alignItems="center">
                    <TextField name="sec5Value" onChange={handleChange} defaultValue="0.00" size="small" sx={{ width: '120px', '& .MuiInputBase-input': { textAlign: 'right' } }} />
                    <Button variant="contained" size="small" sx={{ bgcolor: '#e0e0e0', color: '#000', textTransform: 'none', border: '1px solid #ccc' }}>Table A</Button>
                    <Button variant="contained" size="small" sx={{ bgcolor: '#e0e0e0', color: '#000', textTransform: 'none', border: '1px solid #ccc' }}>Table B</Button>
                </Stack>
            </ModalFormRow>

            <ModalFormRow label="How and when did deceased first acquire the annuity?">
                <TextField name="annuityAcquired" onChange={handleChange} fullWidth size="small" />
            </ModalFormRow>

            <ModalFormRow label="Is this claim iro a donation made mortis causa?">
                <Checkbox name="isDonationMortisCausa" onChange={handleChange} size="small" sx={{ p: 0 }} />
            </ModalFormRow>

            <ModalFormRow label="Is this claim iro a donation made by the Deceased?">
                <Stack direction="row" spacing={1} alignItems="center">
                    <Checkbox name="isDonationByDeceased" onChange={handleChange} size="small" sx={{ p: 0 }} />
                    <Typography variant="caption">(Excluding donations defined in Note 1 of the Rev. 267)</Typography>
                </Stack>
            </ModalFormRow>

            <ModalFormRow label="Selling price of non-listed shares/members interest in CC?">
                <TextField name="sellingPriceShares" onChange={handleChange} defaultValue="0.00" size="small" sx={{ width: '150px', '& .MuiInputBase-input': { textAlign: 'right' } }} />
            </ModalFormRow>

            <ModalFormRow label="Beneficiary becomes owner by nomination agreement?">
                <Checkbox name="isNominationAgreement" onChange={handleChange} size="small" sx={{ p: 0 }} />
            </ModalFormRow>
        </Stack>
                        </>
                    )}

                    {/* ================= TAB 4: ESTIMATED CGT ================= */}
                    {tabValue === 3 && (
                <Stack spacing={0.5}>
                    <ModalFormRow 
                        label="Is this asset subject to Capital Gains Tax?" 
                        note="(For info refer to www.sars.gov.za or press F1)"
                    >
                        <Checkbox name="isCgtApplicable" onChange={handleChange} size="small" sx={{ p: 0 }} />
                    </ModalFormRow>

                    <ModalFormRow label="Valuation as at 1 October 2001">
                        <TextField name="valuation2001" onChange={handleChange} defaultValue="0.00" size="small" sx={{ width: '150px', '& .MuiInputBase-input': { textAlign: 'right' } }} />
                    </ModalFormRow>

                    <ModalFormRow label="What is the calculated roll over base cost for this asset?">
                        <TextField name="baseCost" onChange={handleChange} defaultValue="0.00" size="small" sx={{ width: '150px', '& .MuiInputBase-input': { textAlign: 'right' } }} />
                    </ModalFormRow>

                    <ModalFormRow label="Total exclusions (NOT the annual exclusion)">
                        <TextField name="exclusions" onChange={handleChange} defaultValue="0.00" size="small" sx={{ width: '150px', '& .MuiInputBase-input': { textAlign: 'right' } }} />
                    </ModalFormRow>

                    <ModalFormRow label="Amount of Capital Gain (if applicable)">
                        <Stack direction="row" spacing={2} alignItems="center">
                            <TextField name="capitalGain" onChange={handleChange} defaultValue="0.00" size="small" sx={{ width: '150px', '& .MuiInputBase-input': { textAlign: 'right' } }} />
                            <Button variant="contained" size="small" sx={{ bgcolor: '#e0e0e0', color: '#000', textTransform: 'none', border: '1px solid #b0b0b0' }}>Calculate</Button>
                        </Stack>
                    </ModalFormRow>

                    <ModalFormRow label="Amount of Capital Loss (if applicable)">
                        <Stack direction="row" spacing={2} alignItems="center">
                            <TextField name="capitalLoss" onChange={handleChange} defaultValue="0.00" size="small" sx={{ width: '150px', '& .MuiInputBase-input': { textAlign: 'right' } }} />
                            <Button variant="contained" size="small" sx={{ bgcolor: '#e0e0e0', color: '#000', textTransform: 'none', border: '1px solid #b0b0b0' }}>Calculate</Button>
                        </Stack>
                    </ModalFormRow>

                    <ModalFormRow label="Complete the Asset Type Source Code">
                        <Stack direction="row" spacing={2} alignItems="center">
                            <TextField name="assetSourceCode" onChange={handleChange} fullWidth size="small" variant="outlined" />
                            <Typography variant="caption" sx={{ minWidth: '120px' }}>(e.g. 4250 or 4251)</Typography>
                        </Stack>
                    </ModalFormRow>

                    <ModalFormRow label="Complete the Net Capital Gain/Loss">
                        <Stack direction="row" spacing={2} alignItems="center">
                            <TextField name="netGainLoss" onChange={handleChange} defaultValue="0.00" size="small" sx={{ width: '150px', '& .MuiInputBase-input': { textAlign: 'right' } }} />
                            <Button variant="contained" size="small" sx={{ bgcolor: '#e0e0e0', color: '#000', textTransform: 'none', border: '1px solid #b0b0b0' }}>Calculate</Button>
                        </Stack>
                    </ModalFormRow>

                    <ModalFormRow label="Complete the Inclusion Rate">
                        <Stack direction="row" spacing={1} alignItems="center">
                            <TextField name="inclusionRate" onChange={handleChange} defaultValue="0.00" size="small" sx={{ width: '150px', '& .MuiInputBase-input': { textAlign: 'right' } }} />
                            <Typography variant="caption" sx={{ fontWeight: 'bold' }}>%</Typography>
                        </Stack>
                    </ModalFormRow>

                    <ModalFormRow label="Complete the Taxable Capital Gain/Loss">
                        <Stack direction="row" spacing={2} alignItems="center">
                            <TextField name="taxableGainLoss" onChange={handleChange} defaultValue="0.00" size="small" sx={{ width: '150px', '& .MuiInputBase-input': { textAlign: 'right' } }} />
                            <Button variant="contained" size="small" sx={{ bgcolor: '#e0e0e0', color: '#000', textTransform: 'none', border: '1px solid #b0b0b0' }}>Calculate</Button>
                        </Stack>
                    </ModalFormRow>

                    <ModalFormRow label="What was the Original Purchase Price of the Asset?">
                        <TextField name="originalPurchasePrice" onChange={handleChange} defaultValue="0.00" size="small" sx={{ width: '150px', '& .MuiInputBase-input': { textAlign: 'right' } }} />
                    </ModalFormRow>

                    <ModalFormRow label="Date purchased">
                        <TextField name="datePurchased" onChange={handleChange} size="small" sx={{ width: '150px' }} placeholder="YYYY/MM/DD" />
                    </ModalFormRow>

                    <ModalFormRow label="Complete the method of valuation">
                        <TextField name="valuationMethod" onChange={handleChange} fullWidth size="small" variant="outlined" />
                    </ModalFormRow>

                    <ModalFormRow label="Name of Valuer">
                        <TextField name="valuerName" onChange={handleChange} fullWidth size="small" variant="outlined" />
                    </ModalFormRow>

                    <ModalFormRow label="Complete Valuer's Contact Details">
                        <TextField name="valuerContact" onChange={handleChange} fullWidth size="small" variant="outlined" />
                    </ModalFormRow>
                </Stack>
                    )}
                </Box>
            </DialogContent>

            <DialogActions sx={{ p: 2, bgcolor: '#f5f5f5', borderTop: '1px solid #e0e0e0' }}>
                <Button onClick={handleSave} variant="contained" color="success" size="small" startIcon={<span>✓</span>} sx={{ textTransform: 'none' }}>OK</Button>
                <Button onClick={onClose} variant="contained" color="inherit" size="small" startIcon={<span>X</span>} sx={{ bgcolor: '#e0e0e0', textTransform: 'none' }}>Cancel</Button>
            </DialogActions>
        </Dialog>
    );
}